import React, { useEffect, useState, useRef } from "react";
import { string, bool, func, arrayOf, shape } from "prop-types";
import * as Yup from "yup";

import Drawer from "pages/_components/Drawer/Drawer";
import { Field, Form, Formik } from "formik";
import CustomText from "pages/_components/CustomText/CustomText";
import TextField from "pages/_components/fields/TextField";
import PhoneInput from "pages/_components/fields/PhoneInput";
import Button from "pages/_components/Button";
import * as i18nUtils from "util/i18n";
import * as configUtils from "util/config";
import { validateEmailWithRegex, validatePhone, validateCUILPF } from "util/form";
import Checkbox from "pages/_components/Checkbox";
import { DateRangeField } from "pages/_components/fields/DateRangeField";
import I18n from "pages/_components/I18n";
import Selector from "pages/_components/fields/formik/Selector";
import { connect, useDispatch } from "react-redux";
import { selectors as onboardingSelectors, actions as onboardingActions } from "reducers/onboarding";
import { selectors as environmentSelectors, actions as environmentActions } from "reducers/environment";
import { actions as notificationActions } from "reducers/notification";
import { number } from "yup/lib/locale";
import { getLimitDate } from "util/date";
import CustomData from "pages/_components/CustomData/CustomData";
import DateField from "pages/_components/fields/DateField";
import { differenceInYears } from "date-fns";
import { usePhoneAreaCodesArg } from "../../../hooks/usePhoneAreaCodesArg";
import { useVerifyPersonRoles } from "../hooks/useVerifyPersonRoles";

const FORM_ID = "environment.step.shareholders.signer";

const EnvironmentSignerSlide = ({
    isMobile,
    slideIsVisible,
    closeSlide,
    onSubmit,
    pepJobsList,
    pepRelationshipTypesList,
    nosisCdaRequest,
    verifyPersonRequest,
    shareholders,
    owner,
    isOwner,
    notCreateInRoot,
    fetching,

    maritalStatusList,
    occupationList,
    foreignCountries,
    stateList,
    localityList,
    genders,
    nationalities,
}) => {
    const dispatch = useDispatch();
    const formRef = useRef(null);
    const [submittedValues, setSubmittedValues] = useState({});
    const [capitalHoldingMaxLength, setCapitalHoldingMaxLength] = useState(5);
    const { argAreaCodes } = usePhoneAreaCodesArg();
    const {
        status: nosisCdaRequestStatus,
        value: nosisCdaRequestValue,
        error: nosisCdaRequestError,
        id: nosisCdaRequestId,
    } = nosisCdaRequest;
    const loadingNosis = nosisCdaRequestId === submittedValues?.documentNumber && nosisCdaRequestStatus === "FETCHING";
    const {
        status: verifyPersonRequestStatus,
        value: verifyPersonRequestValue,
        error: verifyPersonRequestError,
        id: verifyPersonRequestId,
    } = verifyPersonRequest;

    const {
        verifiedUser,
        setVerifyUser,
        disabledForm,
        setDisabledForm,
        updatePhone,
        setUpdatePhone,
        verifyPersonRolesRequest,
        verifyPersonRoles,
        onChangeDocument,
        resetForm,
    } = useVerifyPersonRoles();

    const [isSigner, setIsSigner] = useState(true);
    const [disableSignerCheckbox, setDisableSignerCheckbox] = useState(false);
    const [cuil, setCuil] = useState(null);
    const [lastSelectedProvince, setLastSelectedProvince] = useState("");
    const [defaultPhoneNumber] = useState("");

    const loadingVerifyPerson =
        (verifyPersonRequestId === submittedValues?.documentNumber && verifyPersonRequestStatus === "FETCHING") ||
        verifyPersonRolesRequest.status === "FETCHING";
    let suma = owner;
    for (let i = 0; i < shareholders.length; i++) {
        suma = Number(suma) + Number(shareholders[i].capitalHolding) ?? 0;
    }

    const onChangeProvince = (selectedProvince) => {
        if (!!selectedProvince && lastSelectedProvince !== selectedProvince) {
            const stateId = stateList.filter((state) => state.value === selectedProvince)[0].codeId || "";
            formRef.current.setFieldValue("locality", "");
            setLastSelectedProvince(selectedProvince);
            dispatch(onboardingActions.listLocalityRequest("1", Number(stateId)));
        }
    };

    const cleanForm = () => {
        formRef.current.setFieldValue("email", "", false);
        formRef.current.setFieldValue("phoneNumber", "", false);
        formRef.current.setFieldValue("dni", "", false);
        formRef.current.setFieldValue("gender", "", false);
        formRef.current.setFieldValue("street", "", false);
        formRef.current.setFieldValue("streetNumber", "", false);
        formRef.current.setFieldValue("floor", "", false);
        formRef.current.setFieldValue("apartment", "", false);
        formRef.current.setFieldValue("postalCode", "", false);
        formRef.current.setFieldValue("bornDate", "", false);
        formRef.current.setFieldValue("firstName", "", false);
        formRef.current.setFieldValue("lastName", "", false);
        formRef.current.setFieldValue("province", "", false);
        formRef.current.setFieldValue("locality", "", false);
        formRef.current.setFieldValue("nationality", "", false);
        formRef.current.setFieldValue("maritalStatus", "", false);
        formRef.current.setFieldValue("occupation", "", false);
        formRef.current.setFieldValue("fatca", false, false);
        formRef.current.setFieldValue("fatcaNumber1", "", false);
        formRef.current.setFieldValue("fatcaNumber2", "", false);
        formRef.current.setFieldValue("fatcaNumber3", "", false);
        formRef.current.setFieldValue("crsOrOcde", false, false);
        formRef.current.setFieldValue("isLocalResident", "true", false);
        formRef.current.setFieldValue("localResidentNumber", "", false);
        formRef.current.setFieldValue("residentCountry", "", false);
        formRef.current.setFieldValue("foreignResidentNumber", "", false);
        formRef.current.setFieldValue("pep", false, false);
        formRef.current.setFieldValue("isPosition", "true", false);
        formRef.current.setFieldValue("positionType", "", false);
        formRef.current.setFieldValue("positionRange", [null, null], false);
        formRef.current.setFieldValue("relationType", "", false);
    };

    useEffect(() => {
        const {
            status: verifyPersonRolesRequestStatus,
            value: verifyPersonRolesRequestValue,
        } = verifyPersonRolesRequest;
        if (verifyPersonRolesRequestStatus === "SUCCESS") {
            if (formRef && !formRef.current) {
                return;
            }

            const { invitationData } = verifyPersonRolesRequestValue;

            if (isSigner && invitationData) {
                if (invitationData.mobilePhone && invitationData.email) {
                    formRef.current.setFieldValue("phoneNumber", invitationData?.mobilePhone, false);
                    formRef.current.setFieldTouched("phoneNumber", false, false);
                    setUpdatePhone(true);
                    formRef.current.setFieldValue("email", invitationData?.email);
                    formRef.current.setFieldTouched("email", false, false);
                    setDisableSignerCheckbox(invitationData?.isMultiroleSigner);

                    setDisabledForm(true);
                } else {
                    setDisabledForm(false);
                    resetForm(formRef);
                }
            } else if (invitationData) {
                if (invitationData.email) {
                    formRef.current.setFieldValue("email", invitationData.email, false);
                }
                if (invitationData.mobilePhone) {
                    formRef.current.setFieldValue("phoneNumber", invitationData.mobilePhone, false);
                }

                if (invitationData.isMultiroleSigner) {
                    formRef.current.setFieldValue("signer", "true", false);
                    setIsSigner(true);
                    setDisableSignerCheckbox(true);
                } else {
                    formRef.current.setFieldValue("country", "1", false);

                    if (invitationData.documentNumber) {
                        formRef.current.setFieldValue("dni", invitationData.documentNumber, false);
                    }
                    if (invitationData.gender) {
                        formRef.current.setFieldValue("gender", invitationData.gender, false);
                    }
                    if (invitationData.street) {
                        formRef.current.setFieldValue("street", invitationData.street, false);
                    }
                    if (invitationData.streetNumber) {
                        formRef.current.setFieldValue("streetNumber", invitationData.streetNumber, false);
                    }
                    if (invitationData.floor) {
                        formRef.current.setFieldValue("floor", invitationData.floor, false);
                    }
                    if (invitationData.apartment) {
                        formRef.current.setFieldValue("apartment", invitationData.apartment, false);
                    }
                    if (invitationData.postalCode) {
                        formRef.current.setFieldValue("postalCode", invitationData.postalCode, false);
                    }
                    if (invitationData.bornDate) {
                        formRef.current.setFieldValue("bornDate", invitationData.bornDate, false);
                    }
                    if (invitationData.firstName) {
                        formRef.current.setFieldValue("firstName", invitationData.firstName, false);
                    }
                    if (invitationData.lastName) {
                        formRef.current.setFieldValue("lastName", invitationData.lastName, false);
                    }
                    if (invitationData.province) {
                        formRef.current.setFieldValue("province", invitationData.province, false);
                        // Carga el listado de localidades para luego setearle el valor recibido
                        onChangeProvince(invitationData.province);
                    }
                    if (invitationData.locality) {
                        formRef.current.setFieldValue("locality", invitationData.locality, false);
                    }
                    if (invitationData.nationality) {
                        formRef.current.setFieldValue("nationality", invitationData.nationality, false);
                    }
                    if (invitationData.maritalStatus) {
                        formRef.current.setFieldValue("maritalStatus", invitationData.maritalStatus, false);
                    }
                    if (invitationData.occupation) {
                        formRef.current.setFieldValue("occupation", invitationData.occupation, false);
                    }
                    if (invitationData.SSN) {
                        formRef.current.setFieldValue("SSN", invitationData.SSN, false);
                    }
                    if (invitationData.hasPEP) {
                        formRef.current.setFieldValue("hasPEP", invitationData.hasPEP, false);
                    }
                    if (invitationData.pepPosition) {
                        formRef.current.setFieldValue("pepPosition", invitationData.pepPosition, false);
                    }
                    if (invitationData.pepRelation) {
                        formRef.current.setFieldValue("pepRelation", invitationData.pepRelation, false);
                    }
                    if (invitationData.dateToPep) {
                        formRef.current.setFieldValue("dateToPep", invitationData.dateToPep, false);
                    }
                    if (invitationData.dateFromPep) {
                        formRef.current.setFieldValue("dateFromPep", invitationData.dateFromPep, false);
                    }
                    if (invitationData.hasCRS) {
                        formRef.current.setFieldValue("hasCRS", invitationData.hasCRS, false);
                    }
                    if (invitationData.crsLocal) {
                        formRef.current.setFieldValue("crsLocal", invitationData.crsLocal, false);
                    }
                    if (invitationData.crsAbroad) {
                        formRef.current.setFieldValue("crsAbroad", invitationData.crsAbroad, false);
                    }
                    if (invitationData.crsResidentCountry) {
                        formRef.current.setFieldValue("crsResidentCountry", invitationData.crsResidentCountry, false);
                    }
                    setDisabledForm(!invitationData?.isEditable);
                    setDisableSignerCheckbox(invitationData?.isMultiroleSigner);
                }
            } else {
                setDisabledForm(false);
            }
        }
    }, [verifyPersonRolesRequest]);

    const closeSignerSlide = () => {
        setVerifyUser(false);
        setDisabledForm(true);
        resetForm(formRef);
        cleanForm();
        formRef.current.setFieldValue("documentNumber", "", false);
        formRef.current.setFieldValue("capitalHolding", "", false);
        formRef.current.setFieldValue("signer", "true", false);
        closeSlide();
    };

    useEffect(() => {
        if (nosisCdaRequestId === submittedValues?.documentNumber) {
            if (nosisCdaRequestStatus === "SUCCESS" && nosisCdaRequestValue) {
                setSubmittedValues((lastValues) => ({
                    ...lastValues,
                    acceptanceCriteria: nosisCdaRequestValue,
                }));
                dispatch(onboardingActions.resetNosisCda());
            } else if (nosisCdaRequestStatus === "FAILURE") {
                dispatch(onboardingActions.resetNosisCda());
                dispatch(
                    notificationActions.showNotification(i18nUtils.get("", "Error. Firmante invalido."), "error", [
                        "environment",
                    ]),
                );
            }
        }
    }, [
        nosisCdaRequestStatus,
        nosisCdaRequestValue,
        nosisCdaRequestError,
        nosisCdaRequestId,
        submittedValues?.documentNumber,
    ]);

    useEffect(() => {
        if (verifyPersonRequestId === submittedValues?.documentNumber) {
            if (verifyPersonRequestStatus === "SUCCESS" && verifyPersonRequestValue) {
                const { formikBag, ...onlySubmittedValues } = submittedValues;
                onSubmit({
                    ...onlySubmittedValues,
                    ...verifyPersonRequestValue,
                });
                setSubmittedValues({});
                dispatch(
                    notificationActions.showNotification(
                        i18nUtils.get("", "Confirmado. Se agregó accionista/socio."),
                        "success",
                        ["environment"],
                    ),
                );
                dispatch(environmentActions.resetVerifyPerson());
                closeSignerSlide(formikBag);
            } else if (verifyPersonRequestStatus === "FAILURE") {
                dispatch(environmentActions.resetVerifyPerson());
                dispatch(
                    notificationActions.showNotification(i18nUtils.get("", "Error. Firmante invalido."), "error", [
                        "environment",
                    ]),
                );
            }
        }
    }, [verifyPersonRequestStatus, verifyPersonRequestValue, verifyPersonRequestError, verifyPersonRequestId]);

    useEffect(() => {
        const maxLength = configUtils.getInteger("onboarding.capitalHolding.maxLength", 5);
        setCapitalHoldingMaxLength(maxLength);
    }, []);

    const submitIsAllowed = (allFormik) => {
        const {
            documentNumber,
            capitalHolding,
            signer,

            phoneNumber,
            email,

            fatca,
            crsOrOcde,
            pep,
            fatcaNumber1,
            fatcaNumber2,
            fatcaNumber3,
            isLocalResident,
            localResidentNumber,
            residentCountry,
            foreignResidentNumber,
            isPosition,
            positionType,
            positionRange,
            relationRange,
            relationType,

            firstName,
            lastName,
            dni,
            nationality,
            gender,
            bornDate,
            maritalStatus,
            occupation,
            country,
            province,
            locality,
            street,
            streetNumber,
            postalCode,
        } = allFormik.values;

        if (!verifiedUser && !documentNumber) {
            return false;
        }

        if (verifiedUser) {
            if (
                !documentNumber ||
                !capitalHolding ||
                Number.isNaN(capitalHolding) ||
                Number(capitalHolding) + Number(suma) > 100
            ) {
                return false;
            }

            if (signer === "true") {
                if (!phoneNumber.value || !validateEmailWithRegex(email)) {
                    return false;
                }
            } else {
                if (
                    !phoneNumber ||
                    !phoneNumber.value ||
                    !validateEmailWithRegex(email) ||
                    !firstName ||
                    !lastName ||
                    !dni ||
                    !nationality ||
                    !gender ||
                    !bornDate ||
                    !maritalStatus ||
                    !occupation ||
                    !country ||
                    !province ||
                    !locality ||
                    !street ||
                    !streetNumber ||
                    !postalCode
                ) {
                    return false;
                }

                if (!!fatca && (!fatcaNumber1 || !fatcaNumber2 || !fatcaNumber3 || fatcaNumber3?.length < 4)) {
                    return false;
                }
                if (crsOrOcde) {
                    if (isLocalResident === "true" && localResidentNumber.length !== 11) {
                        return false;
                    }
                    if (isLocalResident === "false" && (!residentCountry || !foreignResidentNumber)) {
                        return false;
                    }
                }
                if (pep) {
                    if (isPosition === "true" && (!positionType || !positionRange[0] || !positionRange[1])) {
                        return false;
                    }
                    if (isPosition === "false" && (!relationType || !relationRange[0] || !relationRange[1])) {
                        return false;
                    }
                }
            }
        }

        return true;
    };

    const onChangeSignerRadioBtn = (checkBoxId, allFormik, newVal) => {
        if (newVal === "true") {
            setIsSigner(true);
        } else {
            setIsSigner(false);
        }
        // si se modifica el radio button de firmante, se debe resetear la busqueda de multirol y no onbordeante
        setVerifyUser(false);
        setDisabledForm(true);
        cleanForm();
    };

    const onChangeCheckbox = (checkBoxId, allFormik, newVal) => {
        if (!newVal && checkBoxId === "fatca") {
            allFormik.setFieldValue("fatcaNumber1", "", false);
            allFormik.setFieldValue("fatcaNumber2", "", false);
            allFormik.setFieldValue("fatcaNumber3", "", false);
            allFormik.setFieldTouched("fatcaNumber1", false, false);
            allFormik.setFieldTouched("fatcaNumber2", false, false);
            allFormik.setFieldTouched("fatcaNumber3", false, false);
        } else if (!newVal && checkBoxId === "crsOrOcde") {
            allFormik.setFieldValue("isLocalResident", "true", false);
            allFormik.setFieldValue("localResidentNumber", "", false);
            allFormik.setFieldValue("residentCountry", "", false);
            allFormik.setFieldValue("foreignResidentNumber", "", false);
            allFormik.setFieldTouched("isLocalResident", false, false);
            allFormik.setFieldTouched("localResidentNumber", false, false);
            allFormik.setFieldTouched("residentCountry", false, false);
            allFormik.setFieldTouched("foreignResidentNumber", false, false);
        } else if (!newVal && checkBoxId === "pep") {
            allFormik.setFieldValue("isPosition", "true", false);
            allFormik.setFieldValue("positionType", "", false);
            allFormik.setFieldValue("positionRange", [null, null], false);
            allFormik.setFieldValue("relationType", "", false);
            allFormik.setFieldTouched("isPosition", false, false);
            allFormik.setFieldTouched("positionType", false, false);
            allFormik.setFieldTouched("positionRange", false, false);
            allFormik.setFieldTouched("relationType", false, false);
        }
    };

    const onChangeRadioBtn = (checkBoxId, allFormik, newVal) => {
        if (checkBoxId === "isLocalResident" && newVal === "false") {
            allFormik.setFieldValue("localResidentNumber", "", false);
            allFormik.setFieldTouched("localResidentNumber", false, false);
        } else if (checkBoxId === "isLocalResident" && newVal === "true") {
            allFormik.setFieldValue("residentCountry", "", false);
            allFormik.setFieldValue("foreignResidentNumber", "", false);
            allFormik.setFieldTouched("residentCountry", false, false);
            allFormik.setFieldTouched("foreignResidentNumber", false, false);
        } else if (checkBoxId === "isPosition" && newVal === "false") {
            allFormik.setFieldValue("positionType", "", false);
            allFormik.setFieldValue("positionRange", [null, null], false);
            allFormik.setFieldTouched("positionType", false, false);
            allFormik.setFieldTouched("positionRange", false, false);
        } else if (checkBoxId === "isPosition" && newVal === "true") {
            allFormik.setFieldValue("relationType", "", false);
            allFormik.setFieldTouched("relationType", false, false);
        }
    };

    const requiredString = i18nUtils.get("forms.inputFile.massivePayments.invalid.required");
    const numberTest = (maxLength) =>
        Yup.string()
            .required(requiredString)
            .test(
                "format-number",
                i18nUtils.get("forms.inputFile.massivePayments.invalid.field"),
                (value) =>
                    !!value &&
                    !Number.isNaN(Number(value)) &&
                    !value?.includes(" ") &&
                    (maxLength ? value?.length === maxLength : true),
            );
    const formikProps = {
        validateOnChange: true,
        validateOnBlur: false,
        initialValues: {
            documentNumber: "",
            capitalHolding: "",

            signer: "true",

            phoneNumber: { countryCode: "", areaCode: "", value: "" },
            email: "",

            firstName: "",
            lastName: "",
            dni: "",
            nationality: "",
            gender: "",
            bornDate: "",
            maritalStatus: "",
            occupation: "",
            province: "",
            locality: "",
            street: "",
            streetNumber: "",
            floor: "",
            apartment: "",
            postalCode: "",

            fatca: false,
            fatcaNumber1: "",
            fatcaNumber2: "",
            fatcaNumber3: "",

            crsOrOcde: false,
            isLocalResident: "true",
            localResidentNumber: "",
            residentCountry: "",
            foreignResidentNumber: "",

            pep: false,
            isPosition: "true",
            positionType: "",
            positionRange: [null, null],
            relationRange: [null, null],
            relationType: "",
        },
        // eslint-disable-next-line no-confusing-arrow
        validationSchema: () =>
            // eslint-disable-next-line no-nested-ternary
            !verifiedUser
                ? Yup.object().shape({
                      documentNumber: Yup.string()
                          .required(i18nUtils.get("global.error.required"))
                          .test(
                              "cuil",
                              i18nUtils.get(
                                  "onboarding.step.personalData.cuil.error.invalidFormat",
                                  "Formato de CUIL inválido",
                              ),
                              (value) => validateCUILPF(value),
                          ),
                  })
                : isSigner
                ? Yup.object().shape({
                      documentNumber: Yup.string()
                          .required(i18nUtils.get("global.error.required"))
                          .test(
                              "cuil",
                              i18nUtils.get(
                                  "onboarding.step.personalData.cuil.error.invalidFormat",
                                  "Formato de CUIL inválido",
                              ),
                              (value) => validateCUILPF(value),
                          ),
                      signer: Yup.string().required(requiredString),
                      capitalHolding: Yup.string()
                          .required(requiredString)
                          .test(
                              "less-than-100",
                              i18nUtils.get(
                                  "onboarding.shareholder.capitalHolding.error",
                                  "El porcentaje de capital debe ser mayor a 0 y la suma de porcentajes no de superar el 100% de participación de socios",
                              ),
                              (value) =>
                                  !!value && !Number.isNaN(value) && value > 0 && Number(value) + Number(suma) <= 100,
                          ),
                      phoneNumber: validatePhone(argAreaCodes),
                      email: Yup.string()
                          .required(i18nUtils.get("onboarding.step.verifyEmail.email.error.empty"))
                          .test(
                              "format",
                              i18nUtils.get("onboarding.step.verifyEmail.email.error.invalidFormat"),
                              (value) => !!value && validateEmailWithRegex(value),
                          ),
                  })
                : Yup.object().shape({
                      documentNumber: Yup.string()
                          .required(i18nUtils.get("global.error.required"))
                          .test(
                              "cuil",
                              i18nUtils.get(
                                  "onboarding.step.personalData.cuil.error.invalidFormat",
                                  "Formato de CUIL inválido",
                              ),
                              (value) => validateCUILPF(value),
                          ),
                      signer: Yup.string().required(requiredString),
                      capitalHolding: Yup.string()
                          .required(requiredString)
                          .test(
                              "less-than-100",
                              i18nUtils.get(
                                  "onboarding.shareholder.capitalHolding.error",
                                  "El porcentaje de capital debe ser mayor a 0 y la suma de porcentajes no de superar el 100% de participación de socios",
                              ),
                              (value) =>
                                  !!value && !Number.isNaN(value) && value > 0 && Number(value) + Number(suma) <= 100,
                          ),
                      phoneNumber: validatePhone(argAreaCodes),
                      email: Yup.string()
                          .required(i18nUtils.get("onboarding.step.verifyEmail.email.error.empty"))
                          .test(
                              "format",
                              i18nUtils.get("onboarding.step.verifyEmail.email.error.invalidFormat"),
                              (value) => !!value && validateEmailWithRegex(value),
                          ),
                      firstName: Yup.string().required(requiredString),
                      lastName: Yup.string().required(requiredString),
                      dni: Yup.string()
                          .required(requiredString)
                          .test(
                              "format",
                              i18nUtils.get(
                                  "onboarding.dni.invalid",
                                  "El DNI no se corresponde con el cuil ingresado.",
                              ),
                              (value) => !!value && value.length === 8 && cuil.includes(value),
                          ),
                      nationality: Yup.string().required(requiredString),
                      gender: Yup.string().required(requiredString),
                      bornDate: Yup.date()
                          .required(requiredString)
                          .test("age", i18nUtils.get("onboarding.pj.age.error.message"), (value) => {
                              const minAge = configUtils.getInteger("onboarding.document.age.valid", 18);
                              return differenceInYears(new Date(), new Date(value)) >= minAge;
                          }),
                      maritalStatus: Yup.string().required(requiredString),
                      occupation: Yup.string().required(requiredString),
                      country: Yup.string().required(requiredString),
                      province: Yup.string().required(requiredString),
                      locality: Yup.string().required(requiredString),
                      street: Yup.string().required(requiredString),
                      streetNumber: Yup.string().required(requiredString),
                      floor: Yup.string(),
                      apartment: Yup.string(),
                      postalCode: Yup.string().required(requiredString),

                      fatca: Yup.bool().required(requiredString),
                      fatcaNumber1: Yup.string().when("fatca", { is: true, then: numberTest(3) }),
                      fatcaNumber2: Yup.string().when("fatca", { is: true, then: numberTest(2) }),
                      fatcaNumber3: Yup.string().when("fatca", { is: true, then: numberTest(4) }),

                      crsOrOcde: Yup.bool().required(requiredString),
                      isLocalResident: Yup.string().when("crsOrOcde", {
                          is: true,
                          then: Yup.string().required(requiredString),
                      }),
                      localResidentNumber: Yup.string().when(
                          ["crsOrOcde", "isLocalResident"],
                          (crsOrOcde, isLocalResident, schema) => {
                              if (!!crsOrOcde && isLocalResident === "true") {
                                  return numberTest(11);
                              }
                              return schema;
                          },
                      ),
                      residentCountry: Yup.string().when(
                          ["crsOrOcde", "isLocalResident"],
                          (crsOrOcde, isLocalResident, schema) => {
                              if (!!crsOrOcde && isLocalResident === "false") {
                                  return schema.required(requiredString);
                              }
                              return schema;
                          },
                      ),
                      foreignResidentNumber: Yup.string().when(
                          ["crsOrOcde", "isLocalResident"],
                          (crsOrOcde, isLocalResident, schema) => {
                              if (!!crsOrOcde && isLocalResident === "false") {
                                  return Yup.string().required(requiredString);
                              }
                              return schema;
                          },
                      ),
                      pep: Yup.bool().required(requiredString),
                      isPosition: Yup.string().when("pep", {
                          is: true,
                          then: Yup.string().required(requiredString),
                      }),
                      positionType: Yup.string().when(["pep", "isPosition"], (pep, isPosition, schema) => {
                          if (!!pep && isPosition === "true") {
                              return schema.required(requiredString);
                          }
                          return schema;
                      }),
                      positionRange: Yup.array()
                          .of(Yup.date().nullable())
                          .when(["pep", "isPosition"], (pep, isPosition, schema) => {
                              if (!!pep && isPosition === "true") {
                                  return schema.required(requiredString);
                              }
                              return schema;
                          }),
                      relationRange: Yup.array()
                          .of(Yup.date().nullable())
                          .when(["pep", "isPosition"], (pep, isPosition, schema) => {
                              if (!!pep && isPosition === "false") {
                                  return schema.required(requiredString);
                              }
                              return schema;
                          }),
                      relationType: Yup.string().when(["pep", "isPosition"], (pep, isPosition, schema) => {
                          if (!!pep && isPosition === "false") {
                              return schema.required(requiredString);
                          }
                          return schema;
                      }),
                  }),
        onSubmit: (values, formikBag) => {
            if (values) {
                const found = shareholders.find((element) => element.documentNumber === values.documentNumber);

                if (found) {
                    formikBag.setErrors({
                        documentNumber: i18nUtils.get("", "CUIL ya se encuentra agregado"),
                    });
                    return;
                }
                if (verifiedUser) {
                    setSubmittedValues({
                        formikBag,
                        documentNumber: values.documentNumber,
                        documentType: "CUIL",
                        capitalHolding: values.capitalHolding,
                        signer: isSigner,
                        phoneNumber: values.phoneNumber,
                        email: values?.email,

                        firstName: values?.firstName,
                        lastName: values?.lastName,
                        dni: values?.dni,
                        nationality: values?.nationality,
                        gender: values?.gender,
                        bornDate: values?.bornDate,
                        maritalStatus: values?.maritalStatus,
                        occupation: values?.occupation,
                        country: values?.country,
                        province: values?.province,
                        locality: values?.locality,
                        street: values?.street,
                        streetNumber: values?.streetNumber,
                        floor: values?.floor,
                        apartment: values?.apartment,
                        postalCode: values?.postalCode,

                        fatca: values?.fatca,
                        fatcaNumber1: values?.fatcaNumber1,
                        fatcaNumber2: values?.fatcaNumber2,
                        fatcaNumber3: values?.fatcaNumber3,
                        crsOrOcde: values?.crsOrOcde,
                        localResident: values?.isLocalResident === "true",
                        localResidentNumber: values?.localResidentNumber,
                        residentCountry: values?.residentCountry,
                        foreignResidentNumber: values?.foreignResidentNumber,
                        pep: values?.pep,
                        position: values?.isPosition === "true",
                        positionType: values?.positionType,
                        dateFromPep:
                            values?.isPosition === "true" ? values?.positionRange[0] : values?.relationRange[0],
                        dateToPep: values?.isPosition === "true" ? values?.positionRange[1] : values?.relationRange[1],
                        relationType: values?.relationType,
                        isMultiroleSigner: disableSignerCheckbox,
                        isEditable: disabledForm,
                    });
                    if (isOwner) {
                        dispatch(
                            onboardingActions.getNosisCda({
                                documentNumber: values.documentNumber,
                                documentType: "CUIL",
                                personType: "NATURAL_PERSON",
                            }),
                        );
                    }
                    dispatch(
                        environmentActions.verifyPerson({
                            documentNumber: values.documentNumber,
                            documentType: "CUIL",
                            personType: "NATURAL_PERSON",
                        }),
                    );
                } else {
                    setCuil(values?.documentNumber);
                    verifyPersonRoles(values?.documentNumber, "SOCIO_ACCIONISTA", isSigner);
                }
            }
        },
    };

    return (
        <Formik {...formikProps} ref={formRef}>
            {(allFormik) => (
                <Drawer
                    className="signer-slider"
                    openSidebar={slideIsVisible}
                    notCreateInRoot={notCreateInRoot}
                    toggleSidebar={() => closeSignerSlide(allFormik)}>
                    <div className="container">
                        <h1 className="title-content">
                            <CustomText
                                text={i18nUtils.get("onboarding.regulatoryData.shareholder.title", "Socio/Accionista")}
                            />
                        </h1>
                        <Form>
                            <div>
                                <Field
                                    autoComplete="off"
                                    component={TextField}
                                    idForm={FORM_ID}
                                    maxLength={50}
                                    name="documentNumber"
                                    labelTextDefault={i18nUtils.get("", "CUIL")}
                                    hidePlaceholder
                                    autoFocus={!isMobile}
                                    handleOnChange={(ev) => {
                                        onChangeDocument(ev);
                                        setDisableSignerCheckbox(false);
                                        cleanForm();
                                    }}
                                />
                                <Field
                                    autoComplete="off"
                                    component={TextField}
                                    idForm={FORM_ID}
                                    maxLength={capitalHoldingMaxLength}
                                    name="capitalHolding"
                                    labelTextDefault={i18nUtils.get("", "Tenencia de capital (%)")}
                                    hidePlaceholder
                                    isDouble
                                />
                                <I18n
                                    id=""
                                    defaultValue={i18nUtils.get("", "¿Formarás parte del esquema de firmas?")}
                                />
                                <Field
                                    component={Selector}
                                    renderAs="radio"
                                    inLineControl
                                    idForm={FORM_ID}
                                    name="signer"
                                    options={[
                                        {
                                            id: "true",
                                            label: i18nUtils.get("global.yes", "Si"),
                                        },
                                        {
                                            id: "false",
                                            label: i18nUtils.get("global.no", "No"),
                                        },
                                    ]}
                                    onChange={(v) => onChangeSignerRadioBtn("signer", allFormik, v)}
                                    disabled={disableSignerCheckbox}
                                />
                                {!isSigner && (
                                    <div className="container--alert" role="alert">
                                        <div className="alert alert-info">
                                            <CustomData
                                                icon="information_fill.svg"
                                                titleText={i18nUtils.get(
                                                    "onboarding.step.legalRepresentative.legal",
                                                    "Al seleccionar “No”, tené en cuenta que no tendrá credenciales para realizar operaciones ni consultas.",
                                                )}
                                                keepSpaceIfNoValue={false}
                                            />
                                        </div>
                                    </div>
                                )}
                                {verifiedUser && isSigner && (
                                    <>
                                        <Field
                                            component={PhoneInput}
                                            idForm={FORM_ID}
                                            name="phoneNumber"
                                            country="AR"
                                            mobilePhone="54"
                                            enableSearch={false}
                                            preferredCountries={["ar"]}
                                            labelTextDefault={i18nUtils.get("", "Celular")}
                                            hintLabel={i18nUtils.get(
                                                "",
                                                "Debes incluir el código de área y luego el número.",
                                            )}
                                            disabled={disabledForm}
                                            updatePhone={updatePhone}
                                            phoneAlreadyUpdated={() => setUpdatePhone(false)}
                                        />
                                        <Field
                                            autoComplete="off"
                                            component={TextField}
                                            idForm={FORM_ID}
                                            name="email"
                                            labelTextDefault={i18nUtils.get("", "Email")}
                                            spaceEnabled={false}
                                            hidePlaceholder
                                            disabled={disabledForm}
                                        />
                                    </>
                                )}

                                {verifiedUser && !isSigner && (
                                    <>
                                        <div className="grid-table-row grid-cols-12 col-span-10">
                                            <div className="grid-table-data col-start-2 col-span-5">
                                                <I18n id="" defaultValue="Datos personales" component="b" />
                                            </div>
                                            <br />
                                            <div className="grid-table-data col-start-2 col-span-12">
                                                <I18n
                                                    id=""
                                                    defaultValue="Verificá que los datos sean correctos, en caso contrario podés corregirlos."
                                                />
                                            </div>
                                        </div>
                                        <div className="grid-table-row grid-cols-12 col-span-10">
                                            <div className="grid-table-data col-start-2 col-span-5">
                                                <Field
                                                    component={PhoneInput}
                                                    idForm={FORM_ID}
                                                    name="phoneNumber"
                                                    country="AR"
                                                    mobilePhone={defaultPhoneNumber || "54"}
                                                    enableSearch={false}
                                                    preferredCountries={["ar"]}
                                                    labelTextDefault={i18nUtils.get("", "Celular")}
                                                    hintLabel={i18nUtils.get(
                                                        "",
                                                        "Debes incluir el código de área y luego el número.",
                                                    )}
                                                    updatePhone={updatePhone}
                                                    phoneAlreadyUpdated={() => setUpdatePhone(false)}
                                                />
                                            </div>
                                            <div className="grid-table-data col-start-7 col-span-5">
                                                <Field
                                                    autoComplete="off"
                                                    component={TextField}
                                                    idForm={FORM_ID}
                                                    maxLength={50}
                                                    name="email"
                                                    labelTextDefault={i18nUtils.get("", "Email")}
                                                    spaceEnabled={false}
                                                    hidePlaceholder
                                                />
                                            </div>
                                        </div>
                                        <div className="grid-table-row grid-cols-12 col-span-10">
                                            <div className="grid-table-data col-start-2 col-span-5">
                                                <Field
                                                    autoComplete="off"
                                                    component={TextField}
                                                    idForm={FORM_ID}
                                                    maxLength={50}
                                                    name="firstName"
                                                    labelTextDefault={i18nUtils.get("", "Nombre")}
                                                    hidePlaceholder
                                                    disabled={disabledForm}
                                                />
                                            </div>
                                            <div className="grid-table-data col-start-7 col-span-5">
                                                <Field
                                                    autoComplete="off"
                                                    component={TextField}
                                                    idForm={FORM_ID}
                                                    maxLength={50}
                                                    name="lastName"
                                                    labelTextDefault={i18nUtils.get("", "Apellido")}
                                                    hidePlaceholder
                                                    disabled={disabledForm}
                                                />
                                            </div>
                                            <div className="grid-table-data col-start-2 col-span-5">
                                                <Field
                                                    autoComplete="off"
                                                    component={TextField}
                                                    idForm={FORM_ID}
                                                    maxLength={50}
                                                    name="dni"
                                                    labelTextDefault={i18nUtils.get("", "Documento de identidad")}
                                                    spaceEnabled={false}
                                                    hidePlaceholder
                                                    disabled={disabledForm}
                                                />
                                            </div>
                                            <div className="grid-table-data col-start-7 col-span-5">
                                                <Field
                                                    component={Selector}
                                                    idForm={FORM_ID}
                                                    name="nationality"
                                                    options={
                                                        nationalities.map((value) => ({
                                                            value,
                                                            label: value,
                                                        })) || []
                                                    }
                                                    label={`${i18nUtils.get("", "Nacionalidad")}`}
                                                    placeholder={`${i18nUtils.get("", "Elegir una opción")}`}
                                                    disabled={disabledForm}
                                                />
                                            </div>
                                            <div className="grid-table-data col-start-2 col-span-5">
                                                <Field
                                                    component={Selector}
                                                    idForm={FORM_ID}
                                                    name="gender"
                                                    options={
                                                        genders.map((value) => ({
                                                            value,
                                                            label: i18nUtils.get(
                                                                `onboarding.selectors.pf.genderCode.${value}`,
                                                            ),
                                                        })) || []
                                                    }
                                                    label={`${i18nUtils.get("", "Género/Sexo")}`}
                                                    placeholder={`${i18nUtils.get("", "Elegir una opción")}`}
                                                    disabled={disabledForm}
                                                />
                                            </div>
                                            <div className="grid-table-data col-start-7 col-span-5">
                                                <Field
                                                    component={DateField}
                                                    idForm={FORM_ID}
                                                    name="bornDate"
                                                    labelKey="onboarding.step4.dateOfBirth.label"
                                                    labelText="Fecha de nacimiento"
                                                    minDate={null}
                                                    maxDate={new Date()}
                                                    disabled={disabledForm}
                                                />
                                            </div>
                                            <div className="grid-table-data col-start-2 col-span-5">
                                                <Field
                                                    component={Selector}
                                                    idForm={FORM_ID}
                                                    name="maritalStatus"
                                                    options={maritalStatusList || []}
                                                    label={`${i18nUtils.get("", "Estado civil")}`}
                                                    placeholder={`${i18nUtils.get("", "Elegir una opción")}`}
                                                    disabled={disabledForm}
                                                />
                                            </div>
                                            <div className="grid-table-data col-start-7 col-span-5">
                                                <Field
                                                    component={Selector}
                                                    idForm={FORM_ID}
                                                    name="occupation"
                                                    options={occupationList || []}
                                                    label={`${i18nUtils.get("", "Actividad / ocupación")}`}
                                                    placeholder={`${i18nUtils.get("", "Elegir una opción")}`}
                                                    disabled={disabledForm}
                                                />
                                            </div>
                                        </div>
                                        <br />
                                        <div className="grid-table-row grid-cols-12 col-span-10">
                                            <div className="grid-table-data col-start-2 col-span-5">
                                                <I18n id="" defaultValue="Domicilio" component="b" />
                                            </div>
                                            <br />
                                            <div className="grid-table-data col-start-2 col-span-5">
                                                <Field
                                                    component={Selector}
                                                    idForm={FORM_ID}
                                                    name="country"
                                                    options={[{ value: "1", label: "Argentina" }]}
                                                    label={`${i18nUtils.get(
                                                        "onboarding.personalData.country.label",
                                                        "País",
                                                    )}`}
                                                    placeholder="Argentina"
                                                    disabled
                                                />
                                            </div>
                                            <div className="grid-table-data col-start-7 col-span-5">
                                                <Field
                                                    component={Selector}
                                                    idForm={FORM_ID}
                                                    name="province"
                                                    onChange={onChangeProvince}
                                                    options={stateList}
                                                    label={`${i18nUtils.get(
                                                        "onboarding.personalData.province.label",
                                                        "Provincia",
                                                    )}`}
                                                    placeholder={`${i18nUtils.get(
                                                        "onboarding.personalData.selectorPlaceholder",
                                                        "Elegir una opción",
                                                    )}`}
                                                    disabled={disabledForm}
                                                />
                                            </div>
                                            <div className="grid-table-data col-start-2 col-span-5">
                                                <Field
                                                    component={Selector}
                                                    idForm={FORM_ID}
                                                    name="locality"
                                                    options={localityList}
                                                    label={`${i18nUtils.get(
                                                        "onboarding.personalData.locality.label",
                                                        "Localidad",
                                                    )}`}
                                                    placeholder={`${i18nUtils.get(
                                                        "onboarding.personalData.selectorPlaceholder",
                                                        "Elegir una opción",
                                                    )}`}
                                                    disabled={
                                                        (allFormik?.values?.province !== "" && fetching) || disabledForm
                                                    }
                                                />
                                            </div>
                                            <div className="grid-table-data col-start-7 col-span-5">
                                                <Field
                                                    component={TextField}
                                                    idForm={FORM_ID}
                                                    name="street"
                                                    labelTextDefault={`${i18nUtils.get(
                                                        "onboarding.personalData.street.label",
                                                        "Calle",
                                                    )}`}
                                                    hidePlaceholder
                                                    hasRegex="^[A-Za-z0-9\-_\. ]*$"
                                                    disabled={disabledForm}
                                                />
                                            </div>
                                            <div className="grid-table-data col-start-2 col-span-5">
                                                <Field
                                                    component={TextField}
                                                    idForm={FORM_ID}
                                                    name="streetNumber"
                                                    labelTextDefault={`${i18nUtils.get(
                                                        "onboarding.personalData.streetNumber.label",
                                                        "Número",
                                                    )}`}
                                                    hidePlaceholder
                                                    onlyNumbers
                                                    disabled={disabledForm}
                                                />
                                            </div>
                                            <div className="grid-table-data col-start-7 col-span-5">
                                                <Field
                                                    component={TextField}
                                                    idForm={FORM_ID}
                                                    name="floor"
                                                    labelTextDefault={`${i18nUtils.get(
                                                        "onboarding.personalData.floor.label",
                                                        "Piso",
                                                    )}`}
                                                    hidePlaceholder
                                                    maxLength={2}
                                                    disabled={disabledForm}
                                                />
                                            </div>
                                            <div className="grid-table-data col-start-2 col-span-5">
                                                <Field
                                                    component={TextField}
                                                    idForm={FORM_ID}
                                                    name="apartment"
                                                    labelTextDefault={`${i18nUtils.get(
                                                        "onboarding.personalData.apartment.label",
                                                        "Departamento",
                                                    )}`}
                                                    hidePlaceholder
                                                    hasRegex="^[A-Za-z0-9\-_\. ]*$"
                                                    maxLength={3}
                                                    disabled={disabledForm}
                                                />
                                            </div>
                                            <div className="grid-table-data col-start-7 col-span-5">
                                                <Field
                                                    component={TextField}
                                                    idForm={FORM_ID}
                                                    name="postalCode"
                                                    labelTextDefault={`${i18nUtils.get(
                                                        "onboarding.personalData.postalCode.label",
                                                        "Código postal",
                                                    )}`}
                                                    hidePlaceholder
                                                    onlyNumbers
                                                    disabled={disabledForm}
                                                />
                                            </div>
                                        </div>
                                        <div className="grid-table-row grid-cols-12 col-span-10">
                                            <div className="grid-table-data col-start-2 col-span-10 subtitle">
                                                <I18n id="" defaultValue="Datos adicionales" component="h2" />
                                            </div>

                                            <div className="grid-table-data col-start-2 col-span-10">
                                                <ul className="list">
                                                    <li>
                                                        <Field
                                                            component={Checkbox}
                                                            idForm={FORM_ID}
                                                            name="fatca"
                                                            labelText="enviroment.others.fatca.label"
                                                            labelTextProps={{
                                                                defaultValue:
                                                                    "¿Es residente fiscal en el exterior (FATCA)?",
                                                            }}
                                                            onChange={(v) => onChangeCheckbox("fatca", allFormik, v)}
                                                            disabled={disabledForm}
                                                        />
                                                        {!!allFormik?.values?.fatca && (
                                                            <>
                                                                <p>
                                                                    <I18n
                                                                        id="onboarding.regulatoryData.fatcaNumber.label"
                                                                        defaultValue="Número de seguro social (SSN)"
                                                                    />
                                                                </p>
                                                                <div className="grid grid-cols-3 grid-rows-1 gap-x-3 items-start">
                                                                    <Field
                                                                        component={TextField}
                                                                        idForm={FORM_ID}
                                                                        name="fatcaNumber1"
                                                                        hideLabel
                                                                        placeholder={`${i18nUtils.get(
                                                                            "onboarding.regulatoryData.fatcaNumber1.placeholder",
                                                                            "XXX",
                                                                        )}`}
                                                                        hintLabel={`${i18nUtils.get(
                                                                            "onboarding.regulatoryData.fatcaNumber1.hintLabel",
                                                                            "3 dígitos.",
                                                                        )}`}
                                                                        autoFocus={!isMobile}
                                                                        onlyNumbers
                                                                        maxLength={3}
                                                                        disabled={disabledForm}
                                                                    />
                                                                    <Field
                                                                        component={TextField}
                                                                        idForm={FORM_ID}
                                                                        name="fatcaNumber2"
                                                                        hideLabel
                                                                        placeholder={`${i18nUtils.get(
                                                                            "onboarding.regulatoryData.fatcaNumber2.placeholder",
                                                                            "XX",
                                                                        )}`}
                                                                        hintLabel={`${i18nUtils.get(
                                                                            "onboarding.regulatoryData.fatcaNumber2.hintLabel",
                                                                            "2 dígitos.",
                                                                        )}`}
                                                                        onlyNumbers
                                                                        maxLength={2}
                                                                        disabled={disabledForm}
                                                                    />
                                                                    <Field
                                                                        component={TextField}
                                                                        idForm={FORM_ID}
                                                                        name="fatcaNumber3"
                                                                        hideLabel
                                                                        placeholder={`${i18nUtils.get(
                                                                            "onboarding.regulatoryData.fatcaNumber3.placeholder",
                                                                            "XXXX",
                                                                        )}`}
                                                                        hintLabel={`${i18nUtils.get(
                                                                            "onboarding.regulatoryData.fatcaNumber3.hintLabel",
                                                                            "4 dígitos.",
                                                                        )}`}
                                                                        onlyNumbers
                                                                        maxLength={4}
                                                                        disabled={disabledForm}
                                                                    />
                                                                </div>
                                                            </>
                                                        )}
                                                    </li>
                                                    <li>
                                                        <Field
                                                            component={Checkbox}
                                                            idForm={FORM_ID}
                                                            name="crsOrOcde"
                                                            labelText="enviroment.others.crsOrOcde.label"
                                                            labelTextProps={{
                                                                defaultValue:
                                                                    "¿Es residente fiscal en el exterior (CRS/OCE)?",
                                                            }}
                                                            onChange={(v) =>
                                                                onChangeCheckbox("crsOrOcde", allFormik, v)
                                                            }
                                                            disabled={disabledForm}
                                                        />
                                                        {!!allFormik?.values?.crsOrOcde && (
                                                            <>
                                                                <Field
                                                                    component={Selector}
                                                                    inLineControl
                                                                    renderAs="radio"
                                                                    idForm={FORM_ID}
                                                                    name="isLocalResident"
                                                                    onChange={(v) =>
                                                                        onChangeRadioBtn(
                                                                            "isLocalResident",
                                                                            allFormik,
                                                                            v,
                                                                        )
                                                                    }
                                                                    options={[
                                                                        {
                                                                            id: "true",
                                                                            label: i18nUtils.get(
                                                                                "onboarding.regulatoryData.isLocalResident.true.label",
                                                                                "Residente Local",
                                                                            ),
                                                                        },
                                                                        {
                                                                            id: "false",
                                                                            label: i18nUtils.get(
                                                                                "onboarding.regulatoryData.isLocalResident.false.label",
                                                                                "Residente Exterior",
                                                                            ),
                                                                        },
                                                                    ]}
                                                                    disabled={disabledForm}
                                                                />
                                                                {allFormik?.values?.isLocalResident === "true" ? (
                                                                    <Field
                                                                        component={TextField}
                                                                        idForm={FORM_ID}
                                                                        name="localResidentNumber"
                                                                        labelTextDefault={`${i18nUtils.get(
                                                                            "onboarding.regulatoryData.localResidentNumber.label",
                                                                            "CUIL / CUIT",
                                                                        )}`}
                                                                        placeholder={`${i18nUtils.get(
                                                                            "onboarding.regulatoryData.localResidentNumber.placeholder",
                                                                            "ej: 30403333334",
                                                                        )}`}
                                                                        hintLabel={`${i18nUtils.get(
                                                                            "onboarding.regulatoryData.localResidentNumber.hintLabel",
                                                                            "Debe contener 11 caracteres.",
                                                                        )}`}
                                                                        autoFocus={!isMobile}
                                                                        onlyNumbers
                                                                        disabled={disabledForm}
                                                                    />
                                                                ) : (
                                                                    <>
                                                                        <Field
                                                                            component={Selector}
                                                                            idForm={FORM_ID}
                                                                            name="residentCountry"
                                                                            options={foreignCountries || []}
                                                                            label={`${i18nUtils.get(
                                                                                "onboarding.regulatoryData.residentCountry.label",
                                                                                "País",
                                                                            )}`}
                                                                            placeholder={`${i18nUtils.get(
                                                                                "onboarding.personalData.selectorPlaceholder",
                                                                                "Elegir una opción",
                                                                            )}`}
                                                                            disabled={disabledForm}
                                                                        />
                                                                        <Field
                                                                            component={TextField}
                                                                            idForm={FORM_ID}
                                                                            name="foreignResidentNumber"
                                                                            labelTextDefault={`${i18nUtils.get(
                                                                                "onboarding.regulatoryData.foreignResidentNumber.label",
                                                                                "NIF",
                                                                            )}`}
                                                                            placeholder={`${i18nUtils.get(
                                                                                "onboarding.regulatoryData.fatcaNumber1.placeholder",
                                                                                "ej: 403333333",
                                                                            )}`}
                                                                            onlyNumbers
                                                                            disabled={disabledForm}
                                                                        />
                                                                    </>
                                                                )}
                                                            </>
                                                        )}
                                                    </li>
                                                    <li>
                                                        <Field
                                                            component={Checkbox}
                                                            idForm={FORM_ID}
                                                            name="pep"
                                                            labelText="enviroment.others.pep.label"
                                                            labelTextProps={{
                                                                defaultValue:
                                                                    "¿Es Persona Expuesta Políticamente (PEP)?",
                                                            }}
                                                            onChange={(v) => onChangeCheckbox("pep", allFormik, v)}
                                                            disabled={disabledForm}
                                                        />
                                                        {!!allFormik?.values?.pep && (
                                                            <>
                                                                <Field
                                                                    component={Selector}
                                                                    renderAs="radio"
                                                                    inLineControl
                                                                    idForm={FORM_ID}
                                                                    name="isPosition"
                                                                    onChange={(v) =>
                                                                        onChangeRadioBtn("isPosition", allFormik, v)
                                                                    }
                                                                    options={[
                                                                        {
                                                                            id: "true",
                                                                            label: i18nUtils.get(
                                                                                "onboarding.regulatoryData.isPosition.true.label",
                                                                                "Cargo",
                                                                            ),
                                                                        },
                                                                        {
                                                                            id: "false",
                                                                            label: i18nUtils.get(
                                                                                "onboarding.regulatoryData.isPosition.false.label",
                                                                                "Relación",
                                                                            ),
                                                                        },
                                                                    ]}
                                                                    disabled={disabledForm}
                                                                />
                                                                {allFormik?.values?.isPosition === "true" ? (
                                                                    <>
                                                                        <Field
                                                                            component={Selector}
                                                                            idForm={FORM_ID}
                                                                            name="positionType"
                                                                            options={pepJobsList || []}
                                                                            label={`${i18nUtils.get(
                                                                                "onboarding.regulatoryData.positionType.label",
                                                                                "Tipo de Cargo",
                                                                            )}`}
                                                                            placeholder={`${i18nUtils.get(
                                                                                "onboarding.personalData.selectorPlaceholder",
                                                                                "Elegir una opción",
                                                                            )}`}
                                                                            disabled={disabledForm}
                                                                        />
                                                                        <Field
                                                                            component={DateRangeField}
                                                                            idForm={FORM_ID}
                                                                            defaultLabel="Fecha fin de cargo PEP"
                                                                            minDate={getLimitDate(-3650)}
                                                                            maxDate={getLimitDate(3650)}
                                                                            name="positionRange"
                                                                            dateFormat="dd/MM/yyyy"
                                                                            selectsRange
                                                                            showMonthYearDropdown
                                                                            scrollableMonthYearDropdown
                                                                            popperPlacement="bottom"
                                                                            showGlobalPlaceholder
                                                                            disabled={disabledForm}
                                                                        />
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <Field
                                                                            component={Selector}
                                                                            idForm={FORM_ID}
                                                                            name="relationType"
                                                                            options={pepRelationshipTypesList || []}
                                                                            label={`${i18nUtils.get(
                                                                                "onboarding.regulatoryData.relationType.label",
                                                                                "Tipo de Relación",
                                                                            )}`}
                                                                            placeholder={`${i18nUtils.get(
                                                                                "onboarding.personalData.selectorPlaceholder",
                                                                                "Elegir una opción",
                                                                            )}`}
                                                                            disabled={disabledForm}
                                                                        />
                                                                        <Field
                                                                            component={DateRangeField}
                                                                            idForm={FORM_ID}
                                                                            defaultLabel="Fecha fin de cargo PEP"
                                                                            minDate={getLimitDate(-3650)}
                                                                            maxDate={getLimitDate(3650)}
                                                                            name="relationRange"
                                                                            dateFormat="dd/MM/yyyy"
                                                                            selectsRange
                                                                            showMonthYearDropdown
                                                                            scrollableMonthYearDropdown
                                                                            popperPlacement="bottom"
                                                                            showGlobalPlaceholder
                                                                            disabled={disabledForm}
                                                                        />
                                                                    </>
                                                                )}
                                                            </>
                                                        )}
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                            <div className="grid-table form-actions">
                                <div className="grid-table-row">
                                    <div className="grid-table-data">
                                        <Button
                                            bsStyle="primary"
                                            label={verifiedUser ? "global.add" : "onboarding.cuil.search.button"}
                                            type="submit"
                                            block
                                            disabled={!submitIsAllowed(allFormik)}
                                            loading={
                                                loadingNosis ||
                                                loadingVerifyPerson ||
                                                verifyPersonRolesRequest.status === "FETCHING" ||
                                                fetching
                                            }
                                        />
                                    </div>
                                </div>
                            </div>
                        </Form>
                    </div>
                </Drawer>
            )}
        </Formik>
    );
};

EnvironmentSignerSlide.propTypes = {
    isMobile: bool.isRequired,
    page: string.isRequired,
    slideIsVisible: bool.isRequired,
    isOwner: bool.isRequired,
    closeSlide: func.isRequired,
    onSubmit: func.isRequired,
    owner: number.isRequired,
    notCreateInRoot: bool,
    shareholders: arrayOf(shape({})).isRequired,

    pepJobsList: arrayOf(shape({})).isRequired,
    pepRelationshipTypesList: arrayOf(shape({})).isRequired,

    nosisCdaRequest: arrayOf(shape({})).isRequired,
    verifyPersonRequest: arrayOf(shape({})).isRequired,
    fetching: bool.isRequired,

    maritalStatusList: arrayOf(shape({})).isRequired,
    occupationList: arrayOf(shape({})).isRequired,
    foreignCountries: arrayOf(shape({})).isRequired,
    stateList: arrayOf(shape({})).isRequired,
    localityList: arrayOf(shape({})).isRequired,
    genders: arrayOf(shape({})).isRequired,
    nationalities: arrayOf(shape({})).isRequired,
};

EnvironmentSignerSlide.defaultProps = {
    notCreateInRoot: false,
};

const mapStateToProps = (state) => ({
    pepJobsList: onboardingSelectors.getPepJobsList(state),
    pepRelationshipTypesList: onboardingSelectors.getPepRelationshipTypesList(state),

    nosisCdaRequest: onboardingSelectors.getNosisCdaRequest(state),
    verifyPersonRequest: environmentSelectors.getVerifyPersonRequest(state),
    fetching: onboardingSelectors.getFetching(state),

    maritalStatusList: onboardingSelectors.getMaritalStatusList(state),
    occupationList: onboardingSelectors.getOccupationList(state),
    stateList: onboardingSelectors.getStateList(state),
    localityList: onboardingSelectors.getLocalityList(state),
    foreignCountries: onboardingSelectors.getForeignCountries(state),
    genders: onboardingSelectors.getGenders(state),
    nationalities: onboardingSelectors.getNationalities(state),
});

export default connect(mapStateToProps)(EnvironmentSignerSlide);
